import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FolderOpen, Save, Copy, History, Send, LogOut } from 'lucide-react'
import { useAuth, logout } from '../lib/auth'

interface EditorTopbarProps {
  /** Brief name when one is loaded, undefined otherwise. */
  briefName?: string
  /** Show the "DEMO" play-button badge to the right (for the demo brief). */
  isDemo?: boolean
  /** Save / submit handlers, optional — disabled when unset. */
  onSave?: () => void
  onSaveAs?: () => void
  onOpen?: () => void
  onHistory?: () => void
  onSubmit?: () => void
}

/**
 * The desktop-editor-style toolbar pinned to the top of every
 * authenticated page. Mirrors the original `lorenza.html` shell:
 *   - left: Briefer wordmark + user-email path + brief-status indicator
 *   - right: language flag chips + Open / Save / Save As / History /
 *     Submit / logout
 *
 * CSS lives in `styles/briefer.css` under the `.editor-topbar` namespace.
 */
export function EditorTopbar({
  briefName,
  isDemo,
  onSave,
  onSaveAs,
  onOpen,
  onHistory,
  onSubmit,
}: EditorTopbarProps) {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()

  return (
    <div className="editor-topbar">
      <div className="editor-topbar__brand">
        <a href="/" className="editor-topbar__wordmark" aria-label="Briefer home">
          <img
            src="/brand/lopettia-logomark.svg"
            alt=""
            aria-hidden="true"
            className="editor-topbar__logomark"
          />
          Briefer
        </a>
        {user && (
          <span className="editor-topbar__path">
            <span className="editor-topbar__email">{user.email ?? user.sub.slice(0, 12)}</span>
            <span className="editor-topbar__sep">@</span>
            <span className="editor-topbar__tenant">lopettia-dev</span>
          </span>
        )}
        <span
          className={`editor-topbar__status${briefName ? ' editor-topbar__status--loaded' : ''}`}
        >
          {briefName ?? t('chrome.no_brief', 'no brief loaded')}
        </span>
      </div>

      <div className="editor-topbar__right">
        <div className="editor-topbar__langs" role="group" aria-label={t('common.language')}>
          {(['es', 'en', 'pt'] as const).map((lng) => (
            <button
              type="button"
              key={lng}
              onClick={() => void i18n.changeLanguage(lng)}
              className={`editor-topbar__lang${i18n.language.startsWith(lng) ? ' editor-topbar__lang--active' : ''}`}
              aria-pressed={i18n.language.startsWith(lng)}
            >
              <span className="editor-topbar__flag" aria-hidden="true">
                {FLAGS[lng]}
              </span>
              {lng.toUpperCase()}
            </button>
          ))}
        </div>
        {isDemo && (
          <span className="editor-topbar__demo">
            <span className="editor-topbar__demo-icon" aria-hidden="true">
              ▸
            </span>
            DEMO
          </span>
        )}
        <button
          type="button"
          className="editor-topbar__action"
          onClick={() => (onOpen ? onOpen() : navigate('/briefs'))}
        >
          <FolderOpen size={14} />
          {t('chrome.actions.open', 'Open…')}
        </button>
        <button
          type="button"
          className="editor-topbar__action"
          onClick={onSave}
          disabled={!onSave}
        >
          <Save size={14} />
          {t('chrome.actions.save', 'Save')}
        </button>
        <button
          type="button"
          className="editor-topbar__action"
          onClick={onSaveAs}
          disabled={!onSaveAs}
        >
          <Copy size={14} />
          {t('chrome.actions.save_as', 'Save As…')}
        </button>
        <button
          type="button"
          className="editor-topbar__action"
          onClick={onHistory}
          disabled={!onHistory}
        >
          <History size={14} />
          {t('chrome.actions.history', 'History')}
        </button>
        <button
          type="button"
          className="editor-topbar__submit"
          onClick={onSubmit}
          disabled={!onSubmit}
        >
          <Send size={14} />
          {t('chrome.actions.submit', 'Submit')}
        </button>
        <button
          type="button"
          className="editor-topbar__logout"
          onClick={() => logout()}
          aria-label={t('auth.logout')}
          title={t('auth.logout')}
        >
          <LogOut size={14} />
        </button>
      </div>
    </div>
  )
}

const FLAGS: Record<'es' | 'en' | 'pt', string> = {
  es: '🇪🇸',
  en: '🇺🇸',
  pt: '🇧🇷',
}
